import {
  type TimesheetSettings,
  UpdateTimesheetSettingsInput,
} from '@clockwise/clients/timesheet-settings';
import {
  ApiError,
  type TimesheetLockPeriod,
  type UpcomingLockPeriod,
  upcomingLockDates,
} from '@clockwise/shared';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, Link } from '@tanstack/react-router';
import { type ChangeEvent, Fragment, useEffect } from 'react';
import { type UseFormReturn, useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { BackLink } from '@/components/custom/back-link';
import { nextDeadlineRows } from '@/components/custom/lock-preview';
import { NoAccess } from '@/components/custom/no-access';
import {
  PillTabs,
  PillTabsContent,
  PillTabsList,
  PillTabsTrigger,
} from '@/components/custom/pill-tabs';
import { Section } from '@/components/custom/section';
import { TimesheetUnlockList } from '@/components/custom/timesheet-unlock-list';
import { PageHeader } from '@/components/layout/page-header';
import { PageShell } from '@/components/layout/page-shell';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { timesheetSettingsApi, timesheetUnlocksApi } from '@/lib/api';
import { lastSearch } from '@/lib/last-search';
import { useHasPermission } from '@/lib/permissions';
import { queryKeys } from '@/lib/query-keys';

export const Route = createFileRoute('/_authenticated/timesheets/settings')({
  component: TimesheetSettingsPage,
});

type FormValues = {
  lockEnabled: boolean;
  lockPeriod: TimesheetLockPeriod;
  graceDays: number;
  settleDays: number;
};

const FORM_FIELDS = ['lockEnabled', 'lockPeriod', 'graceDays', 'settleDays'] as const;
function isFormField(s: string): s is (typeof FORM_FIELDS)[number] {
  return (FORM_FIELDS as readonly string[]).includes(s);
}

const DEFAULTS: FormValues = {
  lockEnabled: false,
  lockPeriod: 'weekly',
  graceDays: 0,
  settleDays: 0,
};

/** The stored fields as the form holds them — everything on the settings
 *  payload except the two server-computed lock dates. Seeding and the
 *  post-save re-seed both go through this so a new setting can't be added
 *  to one and forgotten in the other. */
function toFormValues(s: TimesheetSettings): FormValues {
  return {
    lockEnabled: s.lockEnabled,
    lockPeriod: s.lockPeriod,
    graceDays: s.graceDays,
    settleDays: s.settleDays,
  };
}

function TimesheetSettingsPage() {
  const canEdit = useHasPermission('timesheet.settings');
  const queryClient = useQueryClient();

  const settingsQuery = useQuery({
    queryKey: queryKeys.timesheetSettings.current(),
    queryFn: () => timesheetSettingsApi.get(),
    enabled: canEdit,
  });
  const settings: TimesheetSettings | null = settingsQuery.data?.data ?? null;

  // Active-unlock count for the tab badge — a stable, actionable number that
  // doesn't track the in-panel status filter. `limit: 1` because we only need
  // `meta.total`; invalidated alongside the list/form mutations via the shared
  // `timesheetUnlocks.all` key.
  const activeUnlockQuery = useQuery({
    queryKey: queryKeys.timesheetUnlocks.list({ status: 'active', limit: 1, offset: 0 }),
    queryFn: () => timesheetUnlocksApi.list({ status: 'active', limit: 1, offset: 0 }),
    enabled: canEdit,
  });
  const activeUnlockCount = activeUnlockQuery.data?.meta.total ?? 0;

  const form = useForm<FormValues>({
    resolver: zodResolver(UpdateTimesheetSettingsInput),
    defaultValues: DEFAULTS,
  });

  // Seed the form once the saved row (or computed defaults) arrives. The
  // Select is controlled, so it has to mount on the real value rather than
  // receive it post-mount.
  useEffect(() => {
    if (!settings) return;
    form.reset(toFormValues(settings));
  }, [settings, form]);

  const mutation = useMutation({
    mutationFn: (values: FormValues) => timesheetSettingsApi.update(values),
    onSuccess: (res) => {
      toast.success('Timesheet settings saved');
      queryClient.setQueryData(queryKeys.timesheetSettings.current(), res);
      queryClient.invalidateQueries({ queryKey: queryKeys.timesheetSettings.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.audit.all });
      form.reset(toFormValues(res.data));
    },
    onError: (err) => {
      if (err instanceof ApiError && err.fields?.length) {
        for (const fe of err.fields) {
          if (isFormField(fe.path)) form.setError(fe.path, { message: fe.message });
        }
        toast.error('Please fix the highlighted fields');
        return;
      }
      toast.error(err instanceof Error ? err.message : 'Save failed');
    },
  });

  if (!canEdit) {
    return <NoAccess permission="timesheet.settings" resource="Timesheet settings" />;
  }

  const lockEnabled = form.watch('lockEnabled');
  const lockPeriod = form.watch('lockPeriod');
  const graceDays = form.watch('graceDays');
  const settleDays = form.watch('settleDays');
  const errors = form.formState.errors;

  // Live preview of the next 12 periods' lock dates (12 months when monthly,
  // 12 weeks when weekly). Only meaningful while locking is on; recomputed
  // from form values so it tracks edits before save. The first entry is the
  // "next lock date".
  //
  // Both inputs are validated before they reach the preview: react-hook-form
  // hands back NaN for an emptied number field, and a NaN grace count would
  // render "Invalid Date" down the whole schedule rather than an error.
  const previewSchedule =
    lockEnabled && isGraceCount(graceDays) && isGraceCount(settleDays)
      ? upcomingLockDates({ lockPeriod, graceDays, settleDays }, 12)
      : null;
  // Both roles' deadlines are always rendered, including when they fall on
  // the same day at 0 extra days: collapsing them to one column there would
  // hide the fact that a separate approver deadline exists at all, so an
  // operator would have to change the setting to discover the thing the
  // setting controls. Which PERIOD each belongs to is `nextDeadlineRows`'
  // job — they are not always the same one.
  const hasPreview = (previewSchedule?.length ?? 0) > 0;

  const onSubmit = form.handleSubmit((v) => mutation.mutate(v));

  return (
    <PageShell className="gap-6 overflow-y-auto">
      <div>
        <BackLink to="/timesheets">Back to Timesheets</BackLink>
      </div>

      <PageHeader
        title="Timesheet settings"
        subtitle="Configure automatic period locking, or temporarily unlock a locked period."
      />

      <PillTabs defaultValue="auto-lock" className="gap-6 rounded-xl border bg-card p-4">
        <PillTabsList fullWidth>
          <PillTabsTrigger value="auto-lock">Auto-lock</PillTabsTrigger>
          <PillTabsTrigger value="unlock">
            Unlock
            {activeUnlockCount > 0 ? (
              <span className="ml-1 inline-flex items-center rounded-full border border-border bg-background px-2.5 py-0.5 text-xs font-medium text-foreground transition-colors focus:ring-2 focus:ring-ring focus:ring-offset-2 focus:outline-none">
                {activeUnlockCount}
              </span>
            ) : null}
          </PillTabsTrigger>
        </PillTabsList>

        <PillTabsContent value="auto-lock">
          {settingsQuery.isLoading ? (
            <div className="space-y-3" aria-busy="true">
              <Skeleton className="h-9 w-full max-w-md" />
              <Skeleton className="h-9 w-full max-w-md" />
            </div>
          ) : settingsQuery.isError ? (
            <p className="text-sm text-destructive">
              {settingsQuery.error instanceof ApiError
                ? settingsQuery.error.message
                : 'Failed to load timesheet settings.'}
            </p>
          ) : (
            // Wider than the usual max-w-md settings form: the closing
            // schedule carries a period label plus BOTH boundaries' dates, and
            // wrapped every row onto two lines at md. The inputs below keep
            // their own narrower cap so they don't stretch to match the table.
            <form onSubmit={onSubmit} className="flex max-w-3xl flex-col gap-8">
              <Section
                title="Period locking"
                description="When enabled, a period stops accepting new time at its submission deadline, then closes for good at the end of the settlement period that follows."
              >
                <div className="flex items-start gap-3">
                  <Checkbox
                    id="lockEnabled"
                    checked={lockEnabled}
                    onCheckedChange={(v) =>
                      form.setValue('lockEnabled', v === true, { shouldDirty: true })
                    }
                  />
                  <div className="space-y-0.5">
                    <Label htmlFor="lockEnabled">Lock each timesheet period</Label>
                    <p className="text-xs text-muted-foreground">
                      Past periods become read-only after the deadlines below elapse.
                    </p>
                  </div>
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="lockPeriod">Lock period</Label>
                  <Select
                    // Radix Select captures its value at mount and won't cleanly
                    // adopt one applied later (the post-mount reset() once the
                    // saved row loads) — the trigger stays empty and a spurious
                    // change can clobber the field to undefined. Key on the value
                    // so hydration remounts the Select directly onto it.
                    key={lockPeriod ?? 'unset'}
                    value={lockPeriod}
                    onValueChange={(v) =>
                      form.setValue('lockPeriod', v as TimesheetLockPeriod, {
                        shouldDirty: true,
                        shouldValidate: true,
                      })
                    }
                    disabled={!lockEnabled}
                  >
                    <SelectTrigger id="lockPeriod" className="w-full max-w-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="weekly">Weekly</SelectItem>
                      <SelectItem value="monthly">Monthly</SelectItem>
                    </SelectContent>
                  </Select>
                  {errors.lockPeriod?.message ? (
                    <p className="text-xs text-destructive">{errors.lockPeriod.message}</p>
                  ) : null}
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="graceDays">Submission deadline (business days)</Label>
                  <Input
                    id="graceDays"
                    className="max-w-sm"
                    type="text"
                    inputMode="numeric"
                    autoComplete="off"
                    disabled={!lockEnabled}
                    aria-invalid={Boolean(errors.graceDays?.message)}
                    {...registerGraceDays(form, 'graceDays')}
                  />
                  <p className="text-xs text-muted-foreground">
                    Working days a period keeps accepting new time after it ends; weekends aren't
                    counted. It closes at the start of the next working day — 2 gives two extra
                    working days, 0 gives none.
                  </p>
                  {errors.graceDays?.message ? (
                    <p className="text-xs text-destructive">{errors.graceDays.message}</p>
                  ) : null}
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="settleDays">Settlement period (extra business days)</Label>
                  <Input
                    id="settleDays"
                    className="max-w-sm"
                    type="text"
                    inputMode="numeric"
                    autoComplete="off"
                    disabled={!lockEnabled}
                    aria-invalid={Boolean(errors.settleDays?.message)}
                    {...registerGraceDays(form, 'settleDays')}
                  />
                  <p className="text-xs text-muted-foreground">
                    Extra working days to finish what was already submitted — approve, reject, and
                    correct and resubmit rejected entries. New entries and first-time submissions
                    are blocked. Added on top of the submission deadline; 0 closes both at the same
                    time.
                  </p>
                  {errors.settleDays?.message ? (
                    <p className="text-xs text-destructive">{errors.settleDays.message}</p>
                  ) : null}
                </div>

                <div className="rounded-md border bg-muted/30 text-sm">
                  {previewSchedule && hasPreview ? (
                    // ONE grid for both blocks, not two stacked ones. Grid
                    // tracks are scoped to their own container, so two separate
                    // grids size their `auto` date columns independently — and
                    // the top block's dates carry a time, making its columns
                    // wider and leaving the headings visibly out of step with
                    // the schedule below. Sharing a single grid makes the two
                    // sections one table, split only by a rule.
                    <div className={`${LOCK_GRID} px-3 py-2`}>
                      <p className="col-span-3 text-xs font-medium text-muted-foreground">
                        Next lock date
                      </p>
                      <ScheduleHeadings />
                      {nextDeadlineRows(previewSchedule).map((r) => (
                        <Fragment key={r.period.periodEnd}>
                          <span className="text-muted-foreground">
                            {formatPeriodLabel(r.period, lockPeriod)}
                          </span>
                          <span className="text-right font-medium tabular-nums">
                            {r.submission ? (
                              <span data-testid="next-lock-submission">
                                {formatLockDateTime(r.submission)}
                              </span>
                            ) : (
                              <span className="text-muted-foreground">—</span>
                            )}
                          </span>
                          <span className="text-right font-medium tabular-nums">
                            {r.settlement ? (
                              <span data-testid="next-lock-settlement">
                                {formatLockDateTime(r.settlement)}
                              </span>
                            ) : (
                              <span className="text-muted-foreground">—</span>
                            )}
                          </span>
                        </Fragment>
                      ))}
                      <p className="col-span-3 text-xs text-muted-foreground">
                        A period locks at 12:00 AM on its lock date in each employee&apos;s own
                        timezone, so it closes at local midnight for everyone.
                      </p>

                      {/* Negative margin so the rule spans the padded box. */}
                      <div className="col-span-3 -mx-3 my-1 border-t" />

                      <p className="col-span-3 text-xs font-medium text-muted-foreground">
                        Closing schedule (12 {lockPeriod === 'monthly' ? 'months' : 'weeks'} from
                        the earliest period still open)
                      </p>
                      <ScheduleHeadings />
                      {previewSchedule.map((p) => (
                        <Fragment key={p.periodEnd}>
                          <span
                            className="text-muted-foreground"
                            data-testid="lock-schedule-period"
                          >
                            {formatPeriodLabel(p, lockPeriod)}
                          </span>
                          <span className="text-right font-medium tabular-nums">
                            {formatLockDate(p.lockDate)}
                          </span>
                          <span className="text-right font-medium tabular-nums">
                            {formatLockDate(p.settleDate)}
                          </span>
                        </Fragment>
                      ))}
                    </div>
                  ) : (
                    <div className="px-3 py-2">
                      <span className="text-muted-foreground">Next lock date: </span>
                      <span className="text-muted-foreground">
                        {lockEnabled ? '—' : 'Locking is off'}
                      </span>
                    </div>
                  )}
                </div>
              </Section>

              <div className="flex gap-2">
                <Button type="submit" disabled={mutation.isPending || !form.formState.isDirty}>
                  {mutation.isPending ? 'Saving…' : 'Save settings'}
                </Button>
                <Button type="button" variant="outline" asChild disabled={mutation.isPending}>
                  <Link to="/timesheets" search={lastSearch('/timesheets')}>
                    Cancel
                  </Link>
                </Button>
              </div>
            </form>
          )}
        </PillTabsContent>

        <PillTabsContent value="unlock">
          <Section
            title="Unlocks"
            description="Periods that have been temporarily re-opened. Filter by status; each active unlock ends automatically on its auto-end date, or you can edit or end one now."
          >
            <TimesheetUnlockList />
          </Section>
        </PillTabsContent>
      </PillTabs>
    </PageShell>
  );
}

/**
 * The one column definition shared by the "next lock date" row and the
 * upcoming-dates schedule, so the two blocks line up as a single table split
 * by a rule. `auto` on both date columns lets them size to the widest date and
 * gives the rest to the period label, which is the part that wraps first.
 */
const LOCK_GRID = 'grid grid-cols-[1fr_auto_auto] items-baseline gap-x-6 gap-y-1';

/** Column headings for {@link LOCK_GRID}. Rendered by both blocks — repeating
 *  them is what makes each block readable on its own. */
function ScheduleHeadings() {
  return (
    <>
      <span className="text-xs text-muted-foreground">Period</span>
      <span className="text-right text-xs text-muted-foreground">Submission</span>
      <span className="text-right text-xs text-muted-foreground">Settlement</span>
    </>
  );
}

/** A grace-day count the preview can safely do arithmetic with. An emptied
 *  field yields NaN through `valueAsNumber`, and an out-of-range value is
 *  rejected by the resolver but still reaches the render before the error
 *  does — either would produce an "Invalid Date" schedule. */
function isGraceCount(value: number): boolean {
  return Number.isFinite(value) && value >= 0;
}

/**
 * Register a grace-day field as TEXT rather than `type="number"`.
 *
 * A number input mutates its own value on the scroll wheel whenever it holds
 * focus. On this form the Save button sits below the fold, so scrolling down
 * to reach it after typing silently changes what you are about to save — and
 * because the preview recomputes from the field, it agrees with the wrong
 * number and nothing looks amiss. Observed live: 20 typed, 19 saved. The
 * settings here govern tenant-wide locking, so a silent off-by-one decides
 * who can still edit or approve a timesheet.
 *
 * Dropping to text also drops the +/- spinners and the browser's own
 * validation tooltip, which is the same reasoning `CurrencyInput` and the
 * expense cost fields already follow: Zod owns the rules, the input collects
 * text. `valueAsNumber` is kept so the form value stays a number and
 * `UpdateTimesheetSettingsInput` is untouched; the digit filter is what keeps
 * that conversion from producing NaN out of stray letters, leaving an empty
 * field as the only way to reach the "expected number" message — exactly as
 * before this change.
 */
function registerGraceDays(form: UseFormReturn<FormValues>, name: 'graceDays' | 'settleDays') {
  const field = form.register(name, { valueAsNumber: true });
  return {
    ...field,
    onChange: (event: ChangeEvent<HTMLInputElement>) => {
      // Digits only. Deliberately NOT length-capped: a three-character cap
      // turns "0" + typed "365" into "036" and saves 36 — a valid number, so
      // no error, and the preview agrees with it. Range is zod's job.
      event.target.value = event.target.value.replace(/\D/g, '');
      return field.onChange(event);
    },
  };
}

/** The period a schedule row covers: "June 2026" for monthly, the Mon–Sun
 *  range ("Jun 8 – Jun 14, 2026") for weekly. */
function formatPeriodLabel(period: UpcomingLockPeriod, lockPeriod: TimesheetLockPeriod): string {
  const utc = (iso: string) => new Date(`${iso}T00:00:00Z`);
  if (lockPeriod === 'monthly') {
    return utc(period.periodEnd).toLocaleDateString(undefined, {
      month: 'long',
      year: 'numeric',
      timeZone: 'UTC',
    });
  }
  const start = utc(period.periodStart);
  const end = utc(period.periodEnd);
  const sameYear = start.getUTCFullYear() === end.getUTCFullYear();
  const startLabel = start.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    ...(sameYear ? {} : { year: 'numeric' }),
    timeZone: 'UTC',
  });
  const endLabel = end.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  });
  return `${startLabel} – ${endLabel}`;
}

/** YYYY-MM-DD → "Sun, Jun 7, 2026". Parsed as UTC midnight to dodge the
 *  local-timezone off-by-one on plain date strings. */
function formatLockDate(iso: string): string {
  return new Date(`${iso}T00:00:00Z`).toLocaleDateString(undefined, {
    weekday: 'short',
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  });
}

/**
 * YYYY-MM-DD → "Sun, Jun 7, 2026, 12:00 AM" — the date plus the instant the
 * lock actually bites. Enforcement is `todayInZone(owner) >= lockDate`, so a
 * period closes the moment the *owner's* local date reaches the lock date,
 * i.e. at 12:00 AM local. The time is a constant, not a formatted instant:
 * we render UTC midnight so the clock reads 12:00 AM in every locale while
 * the date stays put.
 */
function formatLockDateTime(iso: string): string {
  return new Date(`${iso}T00:00:00Z`).toLocaleString(undefined, {
    weekday: 'short',
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZone: 'UTC',
  });
}
